import I18n from '@/i18n/config';
import { IconName } from '@klp/design/Illustrations';

import type {
    DocumentInfo,
    IDocumentInfo,
    LeaseAgreement,
    ILeaseAgreement,
    ContractLeaseAgreement,
    RawContractLeaseAgreement,
} from './types';

// DocumentInfo utility functions
export const createDocumentInfo = (props: IDocumentInfo): DocumentInfo => {
    return { ...props };
};

export const parseDocumentInfo = (obj: IDocumentInfo): DocumentInfo => {
    try {
        return createDocumentInfo(obj);
    } catch (error) {
        throw new Error(
            `Could not parse Document Info object : ${JSON.stringify(obj)}`,
        );
    }
};

export const createEmptyDocumentInfo = (): DocumentInfo => {
    return parseDocumentInfo({
        id: '',
        name: '',
        contentString: '',
        mimeType: '',
    });
};

// LeaseAgreement utility functions
export const createLeaseAgreement = (
    props: ILeaseAgreement,
): LeaseAgreement => {
    let pricePerM2: string | number = '-';
    let pricePerM2Unit = '';
    let arealUnits = 'm²';
    let areal = props.areal;

    if (Number(props.areal) > 0) {
        pricePerM2 = props.amount / parseInt(props.areal.toString(), 10);
        pricePerM2Unit = I18n.t('kontrakt.prisPerAar');
    } else {
        areal = '-';
        arealUnits = '';
    }

    const pricePerPlace: string | number =
        props.rentalQty > 0 ? props.amount / props.rentalQty : '-';

    return {
        ...props,
        areal,
        pricePerM2,
        pricePerM2Unit,
        pricePerPlace,
        arealUnits,
        isAmountValid: Number(props.amount) > 0,
    };
};

export const parseLeaseAgreement = (obj: ILeaseAgreement): LeaseAgreement => {
    try {
        return createLeaseAgreement(obj);
    } catch (error) {
        throw new Error(
            `Could not parse Contract Registration object : ${JSON.stringify(
                obj,
            )}`,
        );
    }
};

export const createEmptyLeaseAgreement = (): LeaseAgreement => {
    return parseLeaseAgreement({
        amount: 0,
        areal: 0,
        byggNummer: '',
        byggNavn: '',
        kontraktNummer: '',
        kundeNavn: '',
        rentalobjectName: '',
        validFrom: new Date(),
        validTo: new Date(),
        contractLineName: '',
        rentalQty: 0,
        status: '',
        dokuments: [],
        lokalValuta: '',
        arealIkkeMedregnet: false,
        fristOppsigelseLeietaker: new Date(),
        fristOppsigelseUtleier: new Date(),
    });
};

// ContractLeaseAgreement utility functions
export const createContractLeaseAgreement = (
    obj: RawContractLeaseAgreement,
): ContractLeaseAgreement => {
    const kundenavn = obj['kundeNavn'] || '';
    const kundenummer = obj['kundenummer'] || '';

    let leaseagreementList: Array<LeaseAgreement> = [];
    if (obj.kontraktDetaljer?.length && obj.kontraktDetaljer.length > 0) {
        leaseagreementList = obj.kontraktDetaljer.map(
            (leaseAgreement: ILeaseAgreement) => {
                return parseLeaseAgreement(leaseAgreement);
            },
        );
    }

    return {
        kundenavn,
        kundenummer,
        leaseagreementList,
    };
};

export const parseContractLeaseAgreement = (
    obj: unknown,
): ContractLeaseAgreement => {
    try {
        return createContractLeaseAgreement(obj as RawContractLeaseAgreement);
    } catch (error) {
        throw new Error(
            `Could not parse Contract Lease Agreement object : ${JSON.stringify(
                obj,
            )}`,
        );
    }
};

export const createEmptyContractLeaseAgreement = (): ContractLeaseAgreement => {
    return parseContractLeaseAgreement({
        kundeNavn: '',
        kundenummer: '',
        kontraktDetaljer: [],
    });
};

export const getFileTypeIcon = (fileType: string) => {
    switch (fileType.toLowerCase()) {
        case 'application/pdf':
            return IconName.PDF;
        case 'application/msword':
            return IconName.MSDOC;
        case 'application/vnd.ms-excel':
            return IconName.XLS;
        case 'image/jpeg':
            return IconName.JPG;
        case 'image/png':
            return IconName.PNG;
        case 'unknown':
            return IconName.UNKNOWN_FILE;
        default:
            return IconName.UNKNOWN_FILE;
    }
};
